import React, {ChangeEvent, lazy, useEffect} from "react";
import styled, {css} from "styled-components";
import StyledButton from "ui/StyledButton";
import useInput from "hooks/useInput";
import {useTranslation} from "react-i18next";
import {useLoginUserMutation} from "redux/api/userApi";
import {useTypedDispatch, useTypedSelector} from "hooks/useTypedRedux";
import {
    updateAlert,
    updateModalLogin,
    updateModalRegistration,
    updateModalResetPassword
} from "redux/reducers/appSlice";
import {PasswordInput, LoginInput, HelperText, MobileTitle} from "./LoginComponents";
import {ValidationSymbolsMinimum} from "constants/validations";
import {useNavigate} from "react-router-dom";
import {selectIsMobile, selectThemeType, selectUser} from "redux/selectors/appSelector";
import {ProjectSettings} from "config";
import {LoginLogosLight, LoginLogosDark} from "../../constants/logos";


const Login = (): JSX.Element => {

    const {LOGIN_LOGO} = ProjectSettings.other()
    const {t} = useTranslation();
    const navigate = useNavigate()
    const ACCESS = ProjectSettings.access()

    const theme_type = useTypedSelector(selectThemeType)
    const isMobile = useTypedSelector(selectIsMobile)
    const user = useTypedSelector(selectUser)

    const [loginUser, {isSuccess, isError, error}] = useLoginUserMutation()
    const dispatch = useTypedDispatch()

    const username = useInput('', ValidationSymbolsMinimum)
    const password = useInput('', ValidationSymbolsMinimum)

    const inputs = [username, password]

    const CheckInputs = () => {

        inputs.forEach((input) => {
            input.setErrorText('')
        })

        let check = true
        inputs.forEach((input) => {
            if (!input.test()) {
                check = false
                input.setErrorText(t(`common.login.error_validation`))
            }
        })

        if (check) loginUser({login: username.value, password: password.value})
    }

    const ButtonStyle = css`
      margin-bottom: 10px;
      height: 40px;
      max-width: 250px;
    `

    useEffect(() => {
        if (user) navigate('/')
    }, [user])

    useEffect(() => {
        if (isSuccess) {
            if (isMobile) {
                if (sessionStorage.get('lastUrl') === '/login') {
                    navigate('/')
                } else {
                    navigate(-1)
                }
            } else {
                dispatch((updateModalLogin()))
            }
            dispatch(updateAlert({
                isOpen: true,
                status: "success",
                message: t(`common.alert.authorization_success`)
            }))
        }

        if (isError) {
            if (error && 'data' in error) {
                if ('code' in error.data) {
                    password.setErrorText(t(`common.error.${error?.data?.code}`))
                } else {
                    password.setErrorText(error.data.message)
                }
            } else {
                password.setErrorText(t(`common.error.400`))
            }
        }
    }, [isSuccess, isError])

    return (
        <>
            {isMobile
                ? <MobileTitle title={t(`common.login.title`)}/>
                : <>
                    {LOGIN_LOGO &&
                        <Company>
                            {
                                theme_type === "light" ?
                                    <img src={LoginLogosDark[LOGIN_LOGO] ?? ''} alt={"dark-desktop-logo"}
                                         style={{maxHeight: '102px', height: '80px'}}/>
                                    :
                                    <img src={LoginLogosLight[LOGIN_LOGO] ?? ''} alt={"light-desktop-logo"}
                                         style={{maxHeight: '102px', height: '80px'}}/>
                            }

                        </Company>}
                </>
            }
            <Container>
                <ColumnContainer>
                    <LoginInput input={username} onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        username.onChange(e)
                        password.setErrorText('')
                    }}/>
                    <PasswordInput input={password} inputProps={{
                        onKeyDown: async (e) => {
                            if (e.code === "Enter") {
                                e.preventDefault();
                                await CheckInputs();
                            }
                        }
                    }}/>
                </ColumnContainer>

                <StyledButton onClick={CheckInputs}
                              isDisabled={!!password.errorText || !!username.errorText}
                              extra={ButtonStyle}
                              id={'login_button'}>
                    {t(`common.login.login_button`)}
                </StyledButton>

                <HelperColumnContainer>
                    {ACCESS.RECOVERY_PASSWORD.VISIBLE &&
                        <HelperText text={t(`common.login.forgot_password`)} linkText={t(`common.login.click_here`)}
                                    onClick={() => {
                                        if (isMobile) {
                                            navigate('/recovery-password')
                                        } else {
                                            dispatch(updateModalLogin())
                                            dispatch(updateModalResetPassword())
                                        }
                                    }}/>
                    }
                    {ACCESS.REGISTER.VISIBLE &&
                        <HelperText text={t(`common.login.dont_have_account`)}
                                    linkText={t(`common.login.register_here`)}
                                    onClick={() => {
                                        if (isMobile) {
                                            navigate('/register')
                                        } else {
                                            dispatch(updateModalLogin())
                                            dispatch(updateModalRegistration())
                                        }
                                    }}/>
                    }
                </HelperColumnContainer>

            </Container>
        </>
    )
}

export default Login;


const Container = styled.div`
  width: 248px;

  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  justify-items: center;
  row-gap: 30px;
`


const ColumnContainer = styled.form`
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  justify-items: center;
  row-gap: 10px;
`

const HelperColumnContainer = styled.form`
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  justify-items: center;
  row-gap: 10px;
  padding-bottom: 15px;
`

const Company = styled.div`
  width: 100%;
  max-width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
`


